import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, CARD_DESCRIPTOR, siteImages } from '../data/mock';
import { Shield, Cpu } from 'lucide-react';

const PrivacyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Pitch Craft Gear';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="">
        {/* Banner Section */}
        <section className="relative h-64 md:h-72 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={siteImages.privacyBanner}
              alt="Privacy Policy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
          </div>
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-slate-700 text-xs font-semibold mb-4">
                <Shield className="w-3.5 h-3.5" />
                YOUR PRIVACY MATTERS
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-dark-900 font-extrabold mb-3">
                Privacy Policy
              </h1>
              <p className="text-dark-600 max-w-lg">
                How we collect, use, and protect your information
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-dark-50 border border-dark-200 rounded-2xl p-8 md:p-12">
            <div className="prose prose-invert max-w-none">
              <p className="text-dark-600 mb-8">
                At Pitch Craft Gear, operated by {CONTACT_INFO.corpName}, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or make a purchase.
              </p>

              <h2 className="text-xl font-display font-bold text-dark-900 mt-8 mb-4">Information We Collect</h2>
              <p className="text-dark-600 mb-4">
                We collect information you provide directly to us, such as when you:
              </p>
              <ul className="text-dark-600 space-y-2 mb-6 list-disc list-inside">
                <li>Create an account or make a purchase</li>
                <li>Subscribe to our newsletter</li>
                <li>Contact us for support</li>
                <li>Fill out any forms on our website</li>
              </ul>
              <p className="text-dark-600 mb-4">
                This information may include your name, email address, shipping address, phone number, and payment information.
              </p>

              <h2 className="text-xl font-display font-bold text-dark-900 mt-8 mb-4">How We Use Your Information</h2>
              <p className="text-dark-600 mb-4">We use the information we collect to:</p>
              <ul className="text-dark-600 space-y-2 mb-6 list-disc list-inside">
                <li>Process and fulfill your orders</li>
                <li>Send you order confirmations and shipping updates</li>
                <li>Respond to your comments and questions</li>
                <li>Send promotional communications (with your consent)</li>
                <li>Improve our website and customer experience</li>
                <li>Prevent fraud and enhance security</li>
              </ul>

              <h2 className="text-xl font-display font-bold text-dark-900 mt-8 mb-4">Information Sharing</h2>
              <p className="text-dark-600 mb-4">
                We do not sell, trade, or otherwise transfer your personal information to outside parties except:
              </p>
              <ul className="text-dark-600 space-y-2 mb-6 list-disc list-inside">
                <li>To trusted third parties who assist us in operating our website and conducting our business</li>
                <li>When required by law or to protect our rights</li>
                <li>With your consent</li>
              </ul>

              <h2 className="text-xl font-display font-bold text-dark-900 mt-8 mb-4">Payment Security</h2>
              <p className="text-dark-600 mb-4">
                All transactions are processed through secure payment gateways. Your payment information is encrypted and never stored on our servers. Charges will appear as "<span className="text-slate-700 font-semibold">{CARD_DESCRIPTOR}</span>" on your credit card statement.
              </p>

              <h2 className="text-xl font-display font-bold text-dark-900 mt-8 mb-4">Cookies</h2>
              <p className="text-dark-600 mb-4">
                We use cookies to enhance your experience on our website. Cookies help us understand how you interact with our site and allow us to improve our services.
              </p>

              <h2 className="text-xl font-display font-bold text-dark-900 mt-8 mb-4">Your Rights</h2>
              <p className="text-dark-600 mb-4">You have the right to:</p>
              <ul className="text-dark-600 space-y-2 mb-6 list-disc list-inside">
                <li>Access the personal information we have about you</li>
                <li>Request correction of your personal information</li>
                <li>Request deletion of your personal information</li>
                <li>Opt-out of marketing communications</li>
              </ul>

              <h2 className="text-xl font-display font-bold text-dark-900 mt-8 mb-4">Contact Us</h2>
              <p className="text-dark-600 mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="bg-dark-100 border border-dark-200 rounded-xl p-6">
                <p className="text-dark-900 font-semibold">{CONTACT_INFO.corpName}</p>
                <p className="text-dark-600">{CONTACT_INFO.address.street}</p>
                <p className="text-dark-600">{CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</p>
                <p className="text-dark-600 mt-2">Email: <a href={`mailto:${CONTACT_INFO.email}`} className="text-slate-700 hover:underline">{CONTACT_INFO.email}</a></p>
                <p className="text-dark-600">Phone: <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-slate-700 hover:underline">{CONTACT_INFO.phone}</a></p>
              </div>

              <p className="text-dark-500 text-sm mt-8">
                Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPage;
